import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DeepResearchProgressMessage} from 'llm/deep-research/messages/DeepResearchProgress';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import {PURPLE_LIGHTEST} from 'ui-library/Colors';
import {assertNever} from 'util/Types';

import {ChatFailureIndicator} from './ChatFailureIndicator';
import {ClientProgressMessage, ProgressStatus} from './MessageTypes';

/**
 * Render a ClientProgressMessage taking status into account.
 *
 * TODO: Render ClientProgressMessage['progressData'] if present.
 *
 * If the message indicates a progress status, show a loading message.
 * If the message indicates a completed status, show nothing.
 * If the message indicates a failed status, show a failure indicator.
 */
export function ActiveChatProgressMessageBubble({
  message,
  inProgressText = 'Processing your request...',
}: {
  message: ClientProgressMessage;
  inProgressText?: string | Array<string>;
}): JSX.Element | null {
  switch (message.status) {
    case ProgressStatus.STARTED:
    case ProgressStatus.IN_PROGRESS:
      return (
        <div className={css(styles.progressContainer)}>
          <LLMLoadingMessage text={inProgressText} fontSize={14} />
        </div>
      );
    case ProgressStatus.COMPLETED:
      return null;
    case ProgressStatus.FAILED:
      return <ChatFailureIndicator errorMessage={message.errorMessage} />;
    default:
      assertNever(message.status);
      return null;
  }
}

/**
 * Render the appropriate progress message bubble based on the agent type.
 */
export function ChatProgressMessageBubble({message}: {message: ClientProgressMessage}): JSX.Element {
  switch (message.agentType) {
    case AgentType.DEEP_RESEARCH:
      return <DeepResearchProgressMessage message={message} />;
    case AgentType.ASK:
      return (
        <ActiveChatProgressMessageBubble
          message={message}
          inProgressText={[
            'Processing your request...',
            'Analyzing your question...',
            'Gathering information...',
            'Generating answer...',
          ]}
        />
      );
    case AgentType.DATA_CAPTURE:
    case null:
      return <ActiveChatProgressMessageBubble message={message} />;
    default:
      assertNever(message.agentType);
      return <ActiveChatProgressMessageBubble message={message} />;
  }
}

const styles = StyleSheet.create({
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '16px',
    backgroundColor: PURPLE_LIGHTEST,
  },
});
